/*───────────────────────────────────────────────────────────────────────────*\
  File      : src/utils/userAuthApi.ts
  Purpose   : Client-side helper wrapping every /api/user_authentication/*
              endpoint in type-safe Promise functions. Mirrors the pattern
              used by userDataApi.ts so UI developers can import concise
              functions without hand-rolling fetch calls.
\*───────────────────────────────────────────────────────────────────────────*/

import {
  LoginRequest,
  RegisterRequest,
  PasswordResetRequest,
  PasswordResetConfirm,
  VerifyEmailRequest,
  ResendVerificationRequest,
  UserProfileUpdate,
  AuthResponse,
} from "@/src/adapters/gateways/UserAuthenticationGateway";

/*==========================================================================*
 *                         INTERNAL FETCH HELPERS                           *
 *==========================================================================*/

/**
 * Issue a JSON POST request with credentials included.
 *
 * @param path Relative API path.
 * @param payload Request body object.
 * @returns Parsed JSON response.
 */
async function post<T>(path: string, payload: object): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return (await res.json()) as T;
};

/**
 * Issue a JSON PUT request with credentials included.
 *
 * @param path Relative API path.
 * @param payload Request body object.
 * @returns Parsed JSON response.
 */
async function put<T>(path: string, payload: object): Promise<T> {
  const res = await fetch(path, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return (await res.json()) as T;
};

/*==========================================================================*
 *                         CORE AUTHENTICATION FLOWS                        *
 *==========================================================================*/

/**
 * Authenticate user with email/password.
 */
export function login(payload: LoginRequest): Promise<AuthResponse> {
  return post("/api/user_authentication/login", payload);
};

/**
 * Terminate session – optionally include userid for token-less logout.
 */
export function logout(userid?: string): Promise<{ success: boolean; error?: string }> {
  return post("/api/user_authentication/logout", userid ? { userid } : {});
};

/**
 * Refresh access/refresh tokens; optionally supply userid fallback.
 */
export function refreshToken(userid?: string): Promise<AuthResponse> {
  return post("/api/user_authentication/refresh-token", userid ? { userid } : {});
};

/*==========================================================================*
 *                     REGISTRATION & PROFILE MANAGEMENT                    *
 *==========================================================================*/

/**
 * Register new account.
 */
export function register(payload: RegisterRequest): Promise<AuthResponse> {
  return post("/api/user_authentication/register", payload);
};

/**
 * Fetch current (or specified) user profile.
 */
export function getProfile(userid?: string): Promise<AuthResponse> {
  return post("/api/user_authentication/profile", userid ? { userid } : {});
};

/**
 * Update profile fields – NOTE: PUT verb per route definition.
 */
export function updateProfile(payload: UserProfileUpdate & { userid?: string }): Promise<AuthResponse> {
  return put("/api/user_authentication/update-profile", payload);
};

/*==========================================================================*
 *              EMAIL VERIFICATION & PASSWORD RESET FLOWS                   *
 *==========================================================================*/

export function verifyEmail(payload: VerifyEmailRequest): Promise<{ success: boolean; error?: string }> {
  return post("/api/user_authentication/verify-email", payload);
};

export function resendVerificationEmail(
  payload: ResendVerificationRequest,
): Promise<{ success: boolean; error?: string }> {
  return post("/api/user_authentication/resend-verification", payload);
};

export function requestPasswordReset(
  payload: PasswordResetRequest,
): Promise<{ success: boolean; error?: string }> {
  return post("/api/user_authentication/forgot-password", payload);
};

export function confirmPasswordReset(
  payload: PasswordResetConfirm,
): Promise<{ success: boolean; error?: string }> {
  return post("/api/user_authentication/reset-password", payload);
};

/*==========================================================================*
 *                    OAUTH & MAGIC LINK HELPER METHODS                     *
 *==========================================================================*/

/**
 * Google OAuth: send raw ID-token (`credential`) from Google popup.
 */
export function googleOAuth(idToken: string): Promise<AuthResponse> {
  return post("/api/user_authentication/oauth/google", { idToken });
};

/**
 * Send a magic-link sign-in e-mail.
 *
 * @param email Recipient address.
 */
export function sendMagicLink(email: string): Promise<{ success: boolean; error?: string }> {
  return post("/api/user_authentication/magic-link", { email });
};

/**
 * Consume (verify) a magic-link token and obtain JWTs.
 *
 * @param token One-time token from URL query-param.
 */
export function consumeMagicLink(token: string): Promise<AuthResponse> {
  return post("/api/user_authentication/magic-link/consume", { token });
};

/*──────────────────────────── End of file ────────────────────────────────*/
